import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import type { ExtraCategoryType, ExtraType } from "../../../../../../common-deprecated/types/CommonTypes";
import CarDataConsolidator from "../../../../../shared-logic/utils/classes/CarDataConsolidator";
import { find } from "../../../../../../common-deprecated/utils";
import { addExtra, removeExtra, setExtraOption } from "../../../redux/actions/BasketActions";
import { trackCustomizeConfigItem } from "../../../utils/tracking";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import { applyFilterToExtras } from "../../../../../shared-logic/utils/basketUtils";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

type UseExtrasType = {
    categorizedExtras: ExtraCategoryType[];
    onAddExtra: (item: ExtraType, optionId?: string) => void;
    onRemoveExtra: (item: ExtraType) => void;
    onSetExtraOption: (extra: ExtraType, option: string) => void;
    hasActiveFilters: boolean;
};
const useExtras = (): UseExtrasType => {
    const basket = useBnbSelector((state) => state.basket);
    const extrasFilter = useBnbSelector((state) => state.extrasFilter);
    const items = useBnbSelector((state) => state.items);
    const settings = useBnbSelector((state) => state.commonSettings);
    const showPackExtras = useBnbSelector(({ bnbSettings }) => bnbSettings.showPackExtras);
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    const { searchString, appliedCategoriesToFilter, appliedRootCategoryToFilter } = extrasFilter;
    const hasActiveFilters = !!(searchString || appliedCategoriesToFilter.length || appliedRootCategoryToFilter);

    const wheelExtras = items.wheelExtras.filter((wheelExtra) => wheelExtra.category.code === "EXT/TYRES-WHEELS");

    // Extras who are in packs only (These are not included in the items.extras)
    // If showPackExtras is false, extrasOnlyFromPacks are not rendered
    const extrasOnlyFromPacks = showPackExtras
        ? CarDataConsolidator.getExtrasFromPacks(items.packs).filter((packs) => !find(packs.id, items.extras))
        : [];

    const allExtras = [...extrasOnlyFromPacks, ...items.extras].sort((a, b) => a.sortIndex - b.sortIndex);

    // Finance packs are only added to the basket when monthly is toggled
    const financePackEquipment = CarDataConsolidator.getPackEquipment(basket.financePacks);

    const filteredExtras = applyFilterToExtras(
        allExtras,
        searchString,
        appliedCategoriesToFilter,
        appliedRootCategoryToFilter,
    );

    const categorizedExtras: ExtraCategoryType[] = filteredExtras
        .reduce((catExtras: ExtraCategoryType[], extra) => {
            let showExtra =
                !wheelExtras.find((wheelExtra) => wheelExtra.id === extra.id) &&
                // Only show extras which are available for the current colour & upholstery combination.
                CarDataConsolidator.isAvailable(extra, basket.colour, basket.upholstery).available &&
                // Also check the colouring mode
                CarDataConsolidator.validatesForColourMode(extra, {
                    colour: basket.colour,
                    primaryAccentColour: basket.primaryAccentColour,
                    secondaryAccentColour: basket.secondaryAccentColour,
                }) &&
                // Don't show extras which are standard in a financePack if monthly is selected
                !find(extra.id, financePackEquipment) &&
                // Don't show hidden extra's
                !CarDataConsolidator.hideExtra(extra, settings);

            // filterCarOptions shouldn't be shown in the extra's list.
            if (showExtra && items.filterCarOptions) {
                showExtra = !items.filterCarOptions.find((option) => option.shortId === extra.shortId);
            }

            if (extra.category.root && showExtra) {
                const { id, name, code, sortIndex } = extra.category.root;

                const category = find(id, catExtras);
                if (!category) catExtras.push({ id, name, code, sortIndex, extras: [extra] });
                else category.extras.push(extra);
            }
            return catExtras;
        }, [])
        .sort((a, b) => a.sortIndex - b.sortIndex);

    const onAddExtra = (item: ExtraType, optionId = ""): void => {
        trackCustomizeConfigItem(NavStep.Customize, "add-extra");
        dispatch(addExtra(item, false, true, optionId));
    };
    const onRemoveExtra = (item: ExtraType): void => {
        trackCustomizeConfigItem(NavStep.Customize, "remove-extra");
        dispatch(removeExtra(item, false, true));
    };
    const onSetExtraOption = (extra: ExtraType, option: string): void => {
        dispatch(setExtraOption(extra, option, true));
    };

    return {
        categorizedExtras,
        onAddExtra,
        onRemoveExtra,
        onSetExtraOption,
        hasActiveFilters,
    };
};

export default useExtras;
